import React, { useState } from "react";
import { X, Mail, Plus, Trash2 } from "lucide-react";

interface BulkEditData {
  name?: string;
  description?: string;
  condition?: {
    metric?: string;
    operator?: string;
    threshold?: number;
  };
  severity?: string;
  emailAddresses?: string[];
}

interface BulkEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (e: React.FormEvent) => void;
  bulkEditData: BulkEditData;
  setBulkEditData: React.Dispatch<React.SetStateAction<BulkEditData>>;
  selectedRulesCount: number;
  modalLoading: boolean;
}

const metricOptions = [
  { value: "soc", label: "Battery SOC (%)" },
  { value: "battery_voltage", label: "Battery Voltage (V)" },
  { value: "solar_power", label: "Solar Power (W)" },
  { value: "status", label: "Device Status" },
];

const BulkEditModal: React.FC<BulkEditModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  bulkEditData,
  setBulkEditData,
  selectedRulesCount,
  modalLoading,
}) => {
  const [newEmail, setNewEmail] = useState("");

  // Email validation function
  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleAddEmail = () => {
    if (newEmail.trim() && isValidEmail(newEmail.trim())) {
      const currentEmails = bulkEditData.emailAddresses || [];
      if (!currentEmails.includes(newEmail.trim())) {
        setBulkEditData({
          ...bulkEditData,
          emailAddresses: [...currentEmails, newEmail.trim()],
        });
        setNewEmail("");
      }
    }
  };

  const handleRemoveEmail = (emailToRemove: string) => {
    const currentEmails = bulkEditData.emailAddresses || [];
    setBulkEditData({
      ...bulkEditData,
      emailAddresses: currentEmails.filter((email) => email !== emailToRemove),
    });
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddEmail();
    }
  };

  const handleClearAllEmails = () => {
    setBulkEditData({
      ...bulkEditData,
      emailAddresses: [],
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-3xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-lg font-semibold">Bulk Edit Alert Rules</h3>
            <p className="text-sm text-gray-600 mt-1">
              Editing {selectedRulesCount} alert rules
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={onSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Rule Name
            </label>
            <input
              type="text"
              value={bulkEditData.name || ""}
              onChange={(e) =>
                setBulkEditData({ ...bulkEditData, name: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
              placeholder="Keep existing names"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={bulkEditData.description || ""}
              onChange={(e) =>
                setBulkEditData({
                  ...bulkEditData,
                  description: e.target.value,
                })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
              rows={2}
              placeholder="Keep existing descriptions"
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Metric
              </label>
              <select
                value={bulkEditData.condition?.metric || ""}
                onChange={(e) =>
                  setBulkEditData({
                    ...bulkEditData,
                    condition: {
                      ...bulkEditData.condition,
                      metric: e.target.value,
                    },
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
              >
                <option value="">Keep existing</option>
                {metricOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Operator
              </label>
              <select
                value={bulkEditData.condition?.operator || ""}
                onChange={(e) =>
                  setBulkEditData({
                    ...bulkEditData,
                    condition: {
                      ...bulkEditData.condition,
                      operator: e.target.value,
                    },
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
              >
                <option value="">Keep existing</option>
                <option value="gt">Greater than</option>
                <option value="lt">Less than</option>
                <option value="eq">Equal to</option>
                <option value="gte">Greater than or equal</option>
                <option value="lte">Less than or equal</option>
                <option value="ne">Not equal</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Threshold
              </label>
              <input
                type="number"
                value={bulkEditData.condition?.threshold || ""}
                onChange={(e) =>
                  setBulkEditData({
                    ...bulkEditData,
                    condition: {
                      ...bulkEditData.condition,
                      threshold: parseFloat(e.target.value) || 0,
                    },
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                step="0.01"
                placeholder="Keep existing"
              />
            </div>
          </div>

          {/* Email Notification Recipients */}
          <div className="bg-orange-50 rounded-lg p-4">
            <h4 className="text-md font-medium text-orange-900 mb-4 flex items-center">
              <Mail className="w-5 h-5 mr-2" />
              Email Notifications
            </h4>
            <p className="text-sm text-orange-700 mb-4">
              Update email addresses for all selected alert rules. Leave empty
              to keep existing recipients.
            </p>

            {/* Add Email Input */}
            <div className="mb-4">
              <div className="flex gap-2">
                <input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  onKeyPress={handleKeyPress}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-orange-500 focus:border-orange-500 transition-colors"
                  placeholder="Enter email address"
                />
                <button
                  type="button"
                  onClick={handleAddEmail}
                  disabled={!newEmail.trim() || !isValidEmail(newEmail.trim())}
                  className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add
                </button>
              </div>
              {newEmail && !isValidEmail(newEmail) && (
                <p className="text-xs text-red-600 mt-1">
                  Please enter a valid email address
                </p>
              )}
            </div>

            {/* Email List */}
            <div className="space-y-2">
              <div className="flex items-center justify-between mb-2 pb-2 border-b border-orange-200">
                <span className="text-sm font-medium text-orange-900">
                  Recipients ({bulkEditData.emailAddresses?.length || 0})
                </span>
                {(bulkEditData.emailAddresses?.length || 0) > 0 && (
                  <button
                    type="button"
                    onClick={handleClearAllEmails}
                    className="text-xs text-orange-600 hover:text-orange-800"
                  >
                    Clear All
                  </button>
                )}
              </div>

              {(bulkEditData.emailAddresses?.length || 0) === 0 ? (
                <p className="text-sm text-orange-600 text-center py-4">
                  No email addresses added. Existing recipients will be kept.
                </p>
              ) : (
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {bulkEditData.emailAddresses?.map((email, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between bg-white rounded-md p-2 border border-orange-200"
                    >
                      <span className="text-sm text-gray-900">{email}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveEmail(email)}
                        className="text-red-600 hover:text-red-800 p-1"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              disabled={modalLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              disabled={modalLoading}
            >
              {modalLoading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Updating...
                </div>
              ) : (
                `Update ${selectedRulesCount} Rules`
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BulkEditModal;
